import { useEffect, useState } from 'react';
import { Alert, Box, useMediaQuery, useTheme } from '@mui/material';
import { DialogContainer, DialogTitle, DialogBody, DialogActions } from '../../../common';
import SupplyOrderFormSection from './SupplyOrderFormSection';
import { getSupplyOrderApiErrorMessage } from '../utils/supplyOrdersUtils';
import { guardDialogClose } from '../../utils/actionLockUtils';
import { getSupplyOrderDestinationErrors } from '../utils/getSupplyOrderDestinationErrors';
import { SUPPLY_DESTINATION_TEXTS } from '../constants/supplyOrderDestinationConstants';

const SupplyOrderDialog = ({
  open,
  onClose,
  selectedOrder,
  onInputChange,
  onSelectChange,
  onSave,
  warehouses = [],
  warehousesLoading = false,
  providers = [],
  providersLoading = false,
  projects = [],
  projectsLoading = false,
  projectsError,
  onRetryProjects,
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
  isSaving = false,
  isEditMode = false,
  providerOnly = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [errorMessage, setErrorMessage] = useState('');
  const [destinationErrors, setDestinationErrors] = useState({});

  useEffect(() => {
    if (open) {
      setErrorMessage('');
      setDestinationErrors({});
    }
  }, [open]);

  const commonProps = {
    selectedOrder,
    onInputChange: (event) => {
      onInputChange(event);
      setDestinationErrors({});
      if (errorMessage) setErrorMessage('');
    },
    onSelectChange: (event) => {
      onSelectChange(event);
      setDestinationErrors({});
      if (errorMessage) setErrorMessage('');
    },
    isMobile,
    warehouses,
    warehousesLoading,
    providers,
    providersLoading,
    projects,
    projectsLoading,
    projectsError,
    onRetryProjects,
    destinationErrors,
    isSaving,
    searchEnabled: searchEnabled && !selectedOrder?.is_consolidated,
    onAddMaterial,
    onUpdateMaterialQuantity,
    onRemoveMaterial,
    providerOnly,
  };

  const handleSave = async () => {
    const errors = getSupplyOrderDestinationErrors(selectedOrder);
    setDestinationErrors(errors);
    if (Object.keys(errors).length) return;
    try {
      setErrorMessage('');
      const saved = await onSave();
      if (!saved) {
        setErrorMessage(
          SUPPLY_DESTINATION_TEXTS.invalidForm
        );
      }
    } catch (error) {
      console.error('Error al guardar la orden de abastecimiento:', error);
      setErrorMessage(
        getSupplyOrderApiErrorMessage(error, 'No se pudo guardar la orden a proveedor')
      );
    }
  };

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isSaving, onClose)}
      size={isMobile ? 'small' : 'large'}
      minHeight={isMobile ? '70vh' : '60vh'}
    >
      <DialogTitle>
        {isEditMode
          ? 'Editar orden a proveedor'
          : providerOnly
            ? 'Nueva orden a proveedor'
            : 'Nueva orden de abastecimiento'}
      </DialogTitle>

      {errorMessage && (
        <Box sx={{ px: isMobile ? 2 : 3, pt: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <DialogBody>
        <SupplyOrderFormSection {...commonProps} />
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        saveLabel={isEditMode ? 'Guardar cambios' : 'Crear borrador'}
        saveButtonProps={{ isLoading: isSaving }}
      />
    </DialogContainer>
  );
};

export default SupplyOrderDialog;
